/* Selector file rule: root-level functions in this file must be exported selectors only. Move helper functions to lib files. */
import { createSelector } from 'reselect';
import buildCompanyItems from '@/lib/company/buildCompanyItems.js';
import toSafeUserStateString from '@/redux/user/toSafeUserStateString.js';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

export const user = (state) => state?.user || null;
export const userAuth = (state) => user(state)?.auth || null;
export const authSession = (state) => user(state)?.session || null;
export const sessionContextStatus = (state) => user(state)?.sessionContext?.status || 'idle';
export const sessionContextMessage = (state) => user(state)?.sessionContext?.message || null;
export const authSessionStatus = (state) => authSession(state)?.status || 'unknown';
export const authSessionUser = (state) => authSession(state)?.user || null;
export const userActivations = (state) => (Array.isArray(user(state)?.activations) ? user(state).activations : EMPTY_ARRAY);
export const userFeedbackNudgesByCompanyId = (state) => user(state)?.feedbackNudgesByCompanyId || EMPTY_OBJECT;
export const userCompanies = (state) => user(state)?.companies || EMPTY_ARRAY;
export const recentCompanies = (state) => {
  const items = state?.ui?.prefs?.pieve?.recentCompanies;
  return Array.isArray(items) ? items : EMPTY_ARRAY;
};
export const sessionCompanyId = (state) => toSafeUserStateString(user(state)?.sessionCompanyId);
export const authUserId = (state) => toSafeUserStateString(userAuth(state)?.userId);
export const authCompanyId = (state) => toSafeUserStateString(userAuth(state)?.companyId);
export const authCompany = (state) => userAuth(state)?.company || null;
export const authAdmin = (state) => userAuth(state)?.admin === true;
export const authImpersonatorUserId = (state) => toSafeUserStateString(userAuth(state)?.impersonatorUserId);
export const authRole = (state) => toSafeUserStateString(userAuth(state)?.role);
export const authRoleId = (state) => toSafeUserStateString(userAuth(state)?.roleId);
export const authPermissions = (state) => (Array.isArray(userAuth(state)?.permissions) ? userAuth(state).permissions : EMPTY_ARRAY);
export const currentUserActivation = createSelector([userActivations], (activations) => activations[0] || null);
export const hasResolvedUserAuthSnapshot = (state) => Array.isArray(userAuth(state)?.permissions);
export const userId = (state) => authUserId(state);
export const companyId = createSelector([sessionCompanyId, authCompanyId], (currentSessionCompanyId, currentAuthCompanyId) => {
  return currentSessionCompanyId || currentAuthCompanyId || null;
});
export const activeCompany = createSelector([companyId, userCompanies, authCompany], (currentCompanyId, companies, currentAuthCompany) => {
  const normalizedCompanyId = toSafeUserStateString(currentCompanyId);

  if (!normalizedCompanyId) {
    return null;
  }

  const listedCompany =
    (Array.isArray(companies) ? companies : []).find((company) => {
      return toSafeUserStateString(company?.id) === normalizedCompanyId;
    }) || null;

  if (listedCompany) {
    return listedCompany;
  }

  return toSafeUserStateString(currentAuthCompany?.id) === normalizedCompanyId ? currentAuthCompany : null;
});
export const activeCompanyId = createSelector([activeCompany, companyId], (currentActiveCompany, currentCompanyId) => {
  return toSafeUserStateString(currentActiveCompany?.id) || toSafeUserStateString(currentCompanyId);
});
export const isCurrentUserActivationVisible = createSelector([currentUserActivation, activeCompanyId], (activation, currentActiveCompanyId) => {
  return Boolean(currentActiveCompanyId && activation?.companyId === currentActiveCompanyId && ['ready', 'accepted'].includes(activation?.status));
});
export const currentUserFeedbackNudge = createSelector([userFeedbackNudgesByCompanyId, activeCompanyId], (nudgesByCompanyId, currentActiveCompanyId) => {
  return currentActiveCompanyId ? nudgesByCompanyId[currentActiveCompanyId] || null : null;
});
export const isSuperAdmin = createSelector([authRole], (currentRole) => {
  const normalizedRole = String(toSafeUserStateString(currentRole) || '').toLowerCase();
  return normalizedRole === 'super admin';
});
export const impersonatorUserId = (state) => authImpersonatorUserId(state);
export const authIsImpersonation = createSelector([authImpersonatorUserId], (currentImpersonatorUserId) => {
  return Boolean(currentImpersonatorUserId);
});
export const isImpersonation = (state) => authIsImpersonation(state);
export const isPersonalCompany = createSelector([activeCompany], (currentActiveCompany) => {
  return currentActiveCompany?.isPersonal === true;
});
export const companyItems = createSelector([userCompanies, recentCompanies], (companies, recentCompaniesList) => {
  return buildCompanyItems({ companies, recentCompanies: recentCompaniesList });
});
